import React, { useMemo } from "react";
import { useLoginUser, useIsAdmin, useIsGroupNurse } from "@/hooks/useAuth";
import { useGroupSelection } from "@/hooks/useGroupSelection";
import { Info } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import httpClient from "@/config/axios";
import type { GroupResponse } from "@/script/req";

/**
 * 그룹 관리자가 자신의 그룹에서만 페이지에 접근할 수 있도록 하는 HOC
 * 그룹 관리자가 자신의 그룹이 아니면 전체 페이지를 대체하는 메시지를 표시
 */
export function withWantedPermission<P extends object>(
  Component: React.ComponentType<P>,
  options?: {
    message?: string;
  }
) {
  return function WithWantedPermissionComponent(props: P) {
    const loginUser = useLoginUser();
    const { selectedGroupId, groups } = useGroupSelection();
    const isAdmin = useIsAdmin();
    const isGroupNurse = useIsGroupNurse();

    // 그룹 간호사인 경우 별도 API로 그룹 목록 가져오기 (GroupList.tsx와 동일한 로직)
    const { data: groupNurseGroups } = useQuery({
      queryKey: ["auth/switch-group"],
      queryFn: async () => {
        const res = await httpClient.get<GroupResponse[]>(
          "/groups/my-admin-groups"
        );
        return res ?? [];
      },
      staleTime: Infinity,
      gcTime: Infinity,
      enabled: isGroupNurse,
    });

    // 관리자는 groups, 그룹 간호사는 groupNurseGroups 사용
    const availableGroups = isAdmin ? groups : groupNurseGroups;

    // 그룹 관리자인지 확인
    const isGroupAdmin = loginUser?.hn_auth === "HN";
    
    // 그룹 관리자가 아니면 항상 접근 가능
    if (!isGroupAdmin) {
      return <Component {...props} />;
    }
    
    // 그룹 관리자인 경우: 자신의 그룹인지 체크
    const isMyGroupId = loginUser?.original_group_id === selectedGroupId;
    
    // 그룹 관리자가 자신의 그룹이면 접근 가능
    if (isMyGroupId) {
      return <Component {...props} />;
    }

    // 현재 선택된 그룹 이름 찾기
    const currentGroupName = useMemo(() => {
      if (!selectedGroupId || !availableGroups) return null;
      const found = availableGroups.find((g) => g.group_id === selectedGroupId);
      return found?.group_name ?? null;
    }, [selectedGroupId, availableGroups]);

    // 자신의 그룹 이름 찾기
    const myGroupName = useMemo(() => {
      if (!loginUser?.original_group_id || !availableGroups) return null;
      const found = availableGroups.find(
        (g) => g.group_id === loginUser.original_group_id
      );
      return found?.group_name ?? null;
    }, [loginUser?.original_group_id, availableGroups]);

    // 그룹 관리자가 자신의 그룹이 아닐 때 - 전체 페이지를 대체하는 메시지 표시
    const defaultMessage = "이 페이지는 소속 그룹에서만 접근이 가능합니다";
    const message = options?.message || defaultMessage;

    return (
      <div className="w-full flex items-center justify-center bg-gray-50">
        <div className="flex flex-col items-center gap-4 p-8   mx-4">
          <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center">
            <Info className="w-8 h-8 text-gray-400" />
          </div>
          <div className="text-center mb-10">
            <p className="text-gray-600 leading-relaxed mb-4 text-2xl mb-10">
              {message}
            </p>
            <div className="mt-4 space-y-2 text-sm text-gray-500">
              {currentGroupName && (
                <div>
                  현재 선택된 그룹:{" "}
                  <span className="font-semibold text-gray-700">
                    {currentGroupName}
                  </span>
                </div>
              )}
              {myGroupName && (
                <div>
                  소속 그룹:{" "}
                  <span className="font-semibold text-sig">{myGroupName}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };
}
